import React, { useState, useEffect } from 'react';
import { db } from '@/src/services/db';
import { supabase } from '@/src/services/supabaseClient';
import { FormMetadata, FormResponse, FormVersion } from './types';
import { AuthProvider, useAuth } from '@/src/context/AuthContext';
import { useSyncManager } from '@/src/hooks/useSyncManager';

// Auth Components
import Login from './src/components/Login';
import OperatorApp from './src/components/OperatorApp';
import SyncStatus from './src/components/SyncStatus';
import ForcePasswordReset from './src/components/ForcePasswordReset';

// Admin Components
const Dashboard = React.lazy(() => import('./src/components/Dashboard'));
import FormBuilder from './src/components/FormBuilder';
import FormResponder from './src/components/FormResponder';
import FormManager from './src/components/FormManager';
import ResponseBank from './src/components/ResponseBank';
import UserManager from './src/components/UserManager';
const LogisticsManager = React.lazy(() => import('./src/components/LogisticsManager'));
import ParameterConstructor from './src/components/ParameterConstructor/ParameterConstructor';
const AnalyticsManager = React.lazy(() => import('./src/components/AnalyticsManager'));
const SchedulingManager = React.lazy(() => import('./src/components/SchedulingManager'));

import { 
  LayoutDashboard, FilePlus, Menu, LogOut, 
  Files, Database, Users, ShieldAlert, Search, Bell, X, RefreshCw, MapPin, BarChart3, Calculator, CalendarClock
} from 'lucide-react';

type AdminView = 'dashboard' | 'builder' | 'responder' | 'forms' | 'responses' | 'users' | 'audit' | 'logistics' | 'analytics' | 'parameters' | 'scheduling';

const AppContent: React.FC = () => {
  const { user, loading, logout } = useAuth();

  // Initialize Background Sync
  const { pendingCount, isSyncing, lastError, syncNow } = useSyncManager();

  // --- STATE PERSISTENCE ---
  const NAV_STATE_KEY = 'asa_branca_nav_state';

  const [needsReset, setNeedsReset] = useState(false);

  useEffect(() => {
      if (user && user.needsPasswordReset) {
          setNeedsReset(true);
      } else {
          setNeedsReset(false);
      }
  }, [user]);

  const loadNavState = () => {
    try {
        const saved = localStorage.getItem(NAV_STATE_KEY);
        if (saved) return JSON.parse(saved);
    } catch (e) {
        console.error("Failed to load nav state", e);
    }
    return null;
  };

  // --- ADMIN STATE ---
  const [currentView, setCurrentView] = useState<AdminView>(() => loadNavState()?.view || 'dashboard');
  const [selectedFormId, setSelectedFormId] = useState<string | null>(() => loadNavState()?.formId || null);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Data State (Admin)
  const [forms, setForms] = useState<FormMetadata[]>([]);
  const [versions, setVersions] = useState<FormVersion[]>([]);
  const [responses, setResponses] = useState<FormResponse[]>([]);
  const [dashboardStats, setDashboardStats] = useState({ responsesToday: 0, activeForms: 0, refusalRate: 0, avgTime: 'N/A' });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // --- EFFECTS ---

  const refreshData = async () => {
    if (user?.role === 'admin') {
        const [f, r, s, logData] = await Promise.all([
            db.getForms(),
            db.getResponses(undefined, 50), // Fetch full data for first 50 so columns render
            db.getDashboardStats(),
            db.getLogisticsData() // Fetch versions
        ]);
        const allForms = [...f, ...(logData.forms || [])];
        setForms(allForms);
        setResponses(r);
        setDashboardStats(s);
        setVersions(logData.versions);
    }
  };

  useEffect(() => {
    // Only load admin data if user is admin
    if (user?.role === 'admin') {
        refreshData();

        // Realtime Subscription
        const channel = supabase.channel('admin-dashboard')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'forms' }, () => {
                refreshData();
            })
            .on('postgres_changes', { event: '*', schema: 'public', table: 'responses' }, () => {
                refreshData();
            })
            .subscribe();

        // Polling Fallback & Reconnection Handler
        const intervalId = setInterval(refreshData, 60000); // Poll every 60s
        const handleOnline = () => refreshData();
        window.addEventListener('online', handleOnline);

        return () => {
            supabase.removeChannel(channel);
            clearInterval(intervalId);
            window.removeEventListener('online', handleOnline);
        };
    }
  }, [user]);

  // Save navigation state
  useEffect(() => {
      if (user?.role === 'admin') {
        localStorage.setItem(NAV_STATE_KEY, JSON.stringify({ view: currentView, formId: selectedFormId }));
      }
  }, [currentView, selectedFormId, user]);

  // --- NAVIGATION HANDLERS ---
  const handleLogout = async () => {
      localStorage.removeItem(NAV_STATE_KEY);
      await logout();
      // State cleanup is handled by context and component unmount
  };

  if (loading) {
      return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400 font-medium">Carregando sistema...</div>;
  }

  // --- RENDER: LOGIN GATEWAY ---
  if (!user) {
      return <Login />;
  }

  // --- RENDER: OPERATOR APP ---
  if (user.role === 'operator') {
      return (
        <OperatorApp
            user={user}
            onLogout={handleLogout}
            syncStatus={{ pendingCount, isSyncing, lastError, onSync: syncNow }}
        />
      );
  }

  // --- RENDER: ADMIN DESKTOP APP ---

  const navTo = (view: AdminView) => {
    setSelectedFormId(null);
    setCurrentView(view);
    setIsMobileMenuOpen(false);
  };

  const openResponder = (id: string) => {
    setSelectedFormId(id);
    setCurrentView('responder');
  };

  const editForm = (id: string) => {
    setSelectedFormId(id);
    setCurrentView('builder');
  };
  
  if (currentView === 'builder') {
      return (
        <FormBuilder 
            onBack={() => navTo('forms')}
            existingForm={selectedFormId ? forms.find(f => f.id === selectedFormId) : undefined}
        />
      );
  }

  if (currentView === 'responder' && selectedFormId) {
      return (
        <FormResponder 
            formId={selectedFormId}
            onClose={() => navTo('dashboard')}
        />
      );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row font-sans text-slate-900">
      
      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/50 z-40 md:hidden transition-opacity"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-slate-300 flex-shrink-0 flex flex-col h-screen border-r border-slate-800 transform transition-transform duration-200 ease-in-out md:relative md:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-slate-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Asa Branca</span>
              <span className="px-1.5 py-0.5 rounded bg-indigo-900 text-indigo-300 text-[10px] font-mono">PRO</span>
            </div>
            <h1 className="text-lg font-bold text-white tracking-tight flex items-center gap-2">
              <div className="w-6 h-6 bg-indigo-600 rounded flex items-center justify-center text-xs">AB</div>
              Forms Asa Branca
            </h1>
        </div>
        
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
            <div className="px-2 mb-4">
               <SyncStatus
                   pendingCount={pendingCount}
                   isSyncing={isSyncing}
                   lastError={lastError}
                   onSync={syncNow}
               />
            </div>

            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 px-2 mt-2">Painel</div>
            <button onClick={() => navTo('dashboard')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm ${currentView === 'dashboard' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800'}`}>
                <LayoutDashboard size={18} /> Visão Geral
            </button>

            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 px-2 mt-6">Gestão</div>
            <button onClick={() => navTo('forms')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm ${currentView === 'forms' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800'}`}>
                <Files size={18} /> Meus Formulários
            </button>
            <button onClick={() => navTo('responses')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm ${currentView === 'responses' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800'}`}>
                <Database size={18} /> Banco de Respostas
            </button>


            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 px-2 mt-6">Inteligência</div>
            {(user.sectors?.some(s => s.id === 'logistica' || s.id === 'geral') || user.sectorIds?.includes('logistica') || user.sectorIds?.includes('geral')) && (
              <button onClick={() => navTo('logistics')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm ${currentView === 'logistics' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800'}`}>
                  <MapPin size={18} /> Mapa Operacional
              </button>
            )}
            <button onClick={() => navTo('analytics')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm ${currentView === 'analytics' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800'}`}>
                <BarChart3 size={18} /> Analítico & Gráficos
            </button>
            <button onClick={() => navTo('scheduling')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm ${currentView === 'scheduling' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800'}`}>
                <CalendarClock size={18} /> Programação
            </button>
            <button onClick={() => navTo('parameters')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm ${currentView === 'parameters' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800'}`}>
                <Calculator size={18} /> Construção de Parâmetros
            </button>

            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 px-2 mt-6">Sistema</div>
            <button onClick={() => navTo('users')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm ${currentView === 'users' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800'}`}>
                <Users size={18} /> Usuários e Acesso
            </button>
            <button onClick={() => navTo('audit')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm ${currentView === 'audit' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800'}`}>
                <ShieldAlert size={18} /> Logs de Auditoria
            </button>
        </nav>

        <div className="p-4 border-t border-slate-800 bg-slate-900/50">
            <div className="flex items-center gap-3">
                <div className="bg-indigo-500 h-8 w-8 rounded-full flex items-center justify-center text-white font-bold text-xs">
                    {user.name.charAt(0)}
                </div>
                <div className="overflow-hidden flex-1">
                    <p className="text-xs font-medium text-white truncate">{user.name}</p>
                    <p className="text-[10px] text-slate-500 truncate">{user.role} • {user.sector}</p>
                </div>
                <button onClick={handleLogout} className="text-slate-500 hover:text-white" title="Sair"><LogOut size={14}/></button>
            </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto h-screen bg-white relative">
        {/* Top Header (Jakob's Law) */}
        <header className="bg-white border-b sticky top-0 z-20 px-6 py-3 flex justify-between items-center shadow-sm">
            <div className="flex items-center gap-4 w-1/3">
                <div className="relative w-full">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input className="w-full bg-slate-50 border border-slate-200 rounded-md pl-8 py-1.5 text-sm focus:ring-1 focus:ring-indigo-500" placeholder="Buscar formulários, respostas ou usuários..." />
                </div>
            </div>
            <div className="flex items-center gap-4">
                <button
                    onClick={refreshData}
                    className="p-2 hover:bg-slate-50 rounded-full text-slate-400 hover:text-indigo-600 transition-colors"
                    title="Sincronizar Dados"
                >
                    <RefreshCw size={20} />
                </button>

                <div className="relative">
                    <button 
                        onClick={() => setShowNotifications(!showNotifications)}
                        className={`relative p-2 hover:bg-slate-50 rounded-full transition-colors ${showNotifications ? 'text-indigo-600' : 'text-slate-400'}`}
                    >
                        <Bell size={20} />
                        <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                    </button>

                    {showNotifications && (
                        <div className="absolute right-0 top-12 w-80 bg-white shadow-xl rounded-xl border border-slate-100 p-4 animate-in fade-in zoom-in-95 duration-200 z-50">
                            <div className="flex justify-between items-center mb-4 pb-2 border-b border-slate-50">
                                <h3 className="font-bold text-slate-800 text-sm">Notificações</h3>
                                <button onClick={() => setShowNotifications(false)} className="text-slate-400 hover:text-slate-600"><X size={16}/></button>
                            </div>
                            <div className="space-y-3">
                                <div className="flex gap-3 items-start p-2 hover:bg-slate-50 rounded-lg cursor-pointer">
                                    <div className="w-2 h-2 mt-1.5 bg-blue-500 rounded-full flex-shrink-0"></div>
                                    <div>
                                        <p className="text-xs font-bold text-slate-700">Novo Envio de Formulário</p>
                                        <p className="text-[10px] text-slate-500">João Silva enviou "Inspeção Diária" há 5 min.</p>
                                    </div>
                                </div>
                                <div className="flex gap-3 items-start p-2 hover:bg-slate-50 rounded-lg cursor-pointer">
                                    <div className="w-2 h-2 mt-1.5 bg-orange-500 rounded-full flex-shrink-0"></div>
                                    <div>
                                        <p className="text-xs font-bold text-slate-700">Alerta de Recusa</p>
                                        <p className="text-[10px] text-slate-500">2 assinaturas recusadas no setor Manutenção.</p>
                                    </div>
                                </div>
                                <div className="flex gap-3 items-start p-2 hover:bg-slate-50 rounded-lg cursor-pointer">
                                    <div className="w-2 h-2 mt-1.5 bg-slate-300 rounded-full flex-shrink-0"></div>
                                    <div>
                                        <p className="text-xs font-bold text-slate-700">Backup Realizado</p>
                                        <p className="text-[10px] text-slate-500">Backup diário concluído com sucesso.</p>
                                    </div>
                                </div>
                            </div>
                            <button className="w-full mt-4 text-center text-xs text-indigo-600 font-bold hover:underline">
                                Marcar todas como lidas
                            </button>
                        </div>
                    )}
                </div>
                <div className="md:hidden"><Menu size={24} onClick={() => setIsMobileMenuOpen(true)} className="cursor-pointer"/></div>
            </div>
        </header>

        <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
            {currentView === 'dashboard' && (
                <React.Suspense fallback={<div className="p-8 text-center text-gray-500">Carregando Visão Geral...</div>}>
                  <Dashboard forms={forms} responses={responses} stats={dashboardStats} />
                </React.Suspense>
            )}
            
            {currentView === 'forms' && (
                <FormManager 
                    forms={forms} 
                    onCreate={() => { setSelectedFormId(null); setCurrentView('builder'); }}
                    onEdit={editForm}
                    onViewResponses={() => navTo('responses')}
                />
            )}

            {currentView === 'logistics' && (
                <React.Suspense fallback={<div>Loading Logistics...</div>}><LogisticsManager /></React.Suspense>
            )}
            {currentView === 'analytics' && (
                <React.Suspense fallback={<div>Loading Analytics...</div>}><AnalyticsManager /></React.Suspense>
            )}
            {currentView === 'scheduling' && (
                <React.Suspense fallback={<div>Loading Scheduling...</div>}><SchedulingManager /></React.Suspense>
            )}
            {currentView === 'parameters' && (
                <ParameterConstructor forms={forms} versions={versions} />
            )}

            {currentView === 'responses' && (
                <ResponseBank forms={forms} responses={responses} />
            )}

            {(currentView === 'users' || currentView === 'audit') && (
                <UserManager initialTab={currentView === 'audit' ? 'logs' : 'users'} />
            )}
        </div>
      </main>

    </div>
  );
};

const App: React.FC = () => {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
};

export default App;
